import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { MpApprovalSearch } from '../types/marketPromotion.types';
import { toDonationApprovalFilterSearch, toDonationApprovalFilterValues } from '../utils/mpFilters';

const ROUTES = {
  approvals: getRouteApi('/_auth/market-promotion/approvals'),
  mis: getRouteApi('/_auth/market-promotion/mis-approvals'),
} as const;

export type MpApprovalRouteId = keyof typeof ROUTES;

export function useMpApprovalFilters(routeId: MpApprovalRouteId) {
  const route = ROUTES[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toDonationApprovalFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toDonationApprovalFilterValues(toDonationApprovalFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDonationApprovalFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): MpApprovalSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, params };
}
